import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerCTA from "../components/ExpenseTrackerCTA";

export default function CalculatorsIndex() {
  return (
    <main className="mx-auto max-w-6xl px-4 md:px-6 py-8">
      <Helmet>
        <title>Free Calculators for Freelancers - Tax, Retirement & Inflation Tools | Free1099Calc</title>
        <meta
          name="description"
          content="Free financial calculators for 1099 workers, freelancers, and contractors. Estimate taxes, plan retirement with real returns, and understand inflation impact on your business."
        />
        <meta name="keywords" content="freelance calculators, 1099 tax calculator, retirement calculator, inflation calculator, self-employment tools, contractor financial planning, freelance rate calculator, quarterly tax estimator" />
        <link rel="canonical" href="https://free1099calc.com/calculators" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Free Financial Calculators for Freelancers & Contractors" />
        <meta property="og:description" content="Plan taxes, retirement, and inflation impact with free calculators designed for 1099 workers and independent contractors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/calculators" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Free Calculators for Freelancers" />
        <meta name="twitter:description" content="Tax, retirement, and inflation calculators for 1099 workers and independent contractors." />
      </Helmet>

      <section className="mb-10 rounded-3xl border-2 border-gray-200 bg-gradient-to-br from-primary-600 via-primary-700 to-blue-900 p-10 md:p-14 shadow-2xl">
        <div className="text-center">
          <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-5 py-2 mb-6">
            <svg className="w-5 h-5 text-white mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" />
            </svg>
            <span className="text-sm font-bold text-white">FREE FINANCIAL TOOLS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 text-white leading-tight">Calculators Built for Freelancers</h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-5 max-w-4xl mx-auto leading-relaxed">
            Quick, transparent financial planning tools designed specifically for <strong className="text-white">1099 workers, freelancers, 
            and independent contractors</strong>. All calculators work in your browser with no signup required.
          </p>
          <p className="text-lg md:text-xl text-blue-200 max-w-3xl mx-auto leading-relaxed">
            Every calculator focuses on <strong className="text-white">real, inflation-adjusted dollars</strong> so you can plan with 
            confidence. From quarterly tax estimates to retirement projections, these tools help you understand 
            your financial picture and make informed decisions.
          </p>
        </div>
      </section>

      {/* Featured Expense Tracker */}
      <section className="mb-12">
        <ExpenseTrackerCTA />
      </section>

      <section className="grid gap-8 md:grid-cols-3 mb-12">
        <Link to="/" className="group block rounded-3xl border-2 border-blue-200 bg-white p-8 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          <div className="mb-4 p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl inline-block group-hover:from-blue-600 group-hover:to-blue-700 transition-all">
            <svg className="w-12 h-12 text-blue-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">1099 Tax Calculator</h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Estimate federal income tax, self-employment tax (15.3%), and state taxes. Get quarterly payment amounts 
            for April, June, September, and January deadlines.
          </p>
          <div className="flex items-center text-blue-600 font-semibold group-hover:gap-2 transition-all">
            Calculate Taxes
            <svg className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </Link>

        <Link to="/retirement" className="group block rounded-3xl border-2 border-green-200 bg-white p-8 hover:border-green-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          <div className="mb-4 p-3 bg-gradient-to-br from-green-100 to-emerald-200 rounded-2xl inline-block group-hover:from-green-600 group-hover:to-emerald-700 transition-all">
            <svg className="w-12 h-12 text-green-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">Retirement Calculator</h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Project retirement savings with inflation-adjusted returns. Includes employer match calculations, 
            4% safe withdrawal rate, and monthly savings targets in today's dollars.
          </p>
          <div className="flex items-center text-green-600 font-semibold group-hover:gap-2 transition-all">
            Plan Retirement
            <svg className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </Link>

        <Link to="/calculators/inflation" className="group block rounded-3xl border-2 border-purple-200 bg-white p-8 hover:border-purple-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          <div className="mb-4 p-3 bg-gradient-to-br from-purple-100 to-pink-200 rounded-2xl inline-block group-hover:from-purple-600 group-hover:to-pink-700 transition-all">
            <svg className="w-12 h-12 text-purple-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">Inflation Calculator</h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Visualize U.S. CPI trends over decades and project future purchasing power. Understand how inflation 
            erodes the value of money and why you need to raise rates annually.
          </p>
          <div className="flex items-center text-purple-600 font-semibold group-hover:gap-2 transition-all">
            Analyze Inflation
            <svg className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </Link>
      </section>

      {/* Educational Content */}
      <section className="rounded-3xl border-2 border-gray-200 bg-gradient-to-br from-white via-gray-50 to-blue-50 p-8 md:p-12 mb-10 shadow-xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900">Why Freelancers Need These Financial Tools</h2>
        </div>
        
        <div className="space-y-8 text-gray-700">
          <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-blue-100 shadow-md">
            <h3 className="text-2xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <svg className="w-7 h-7 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1zm1-4a1 1 0 100 2h.01a1 1 0 100-2H7zm2 1a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm4-4a1 1 0 100 2h.01a1 1 0 100-2H13zM9 9a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zM7 8a1 1 0 000 2h.01a1 1 0 000-2H7z" clipRule="evenodd" />
              </svg>
              The Unique Financial Challenges of 1099 Work
            </h3>
            <p className="mb-4 leading-relaxed">
              Traditional W-2 employees have taxes automatically withheld, employer-matched retirement contributions, 
              and predictable paychecks. As a <strong className="text-blue-700">freelancer or independent contractor</strong>, you're responsible 
              for all of these yourself:
            </p>
            <ul className="space-y-3 mb-4">
              <li className="flex gap-3">
                <span className="text-blue-600 text-xl flex-shrink-0">•</span>
                <div>
                  <strong className="text-gray-900">Self-employment tax:</strong> You pay both employer and employee portions of Social Security and Medicare (15.3% total)
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-blue-600 text-xl flex-shrink-0">•</span>
                <div>
                  <strong className="text-gray-900">Quarterly estimated taxes:</strong> Must prepay taxes four times per year to avoid penalties
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-blue-600 text-xl flex-shrink-0">•</span>
                <div>
                  <strong className="text-gray-900">Retirement savings:</strong> No employer 401(k) or pension—you must set up and fund your own SEP IRA or Solo 401(k)
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-blue-600 text-xl flex-shrink-0">•</span>
                <div>
                  <strong className="text-gray-900">Irregular income:</strong> Variable monthly earnings make budgeting and tax planning more complex
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-blue-600 text-xl flex-shrink-0">•</span>
                <div>
                  <strong className="text-gray-900">No benefits:</strong> Health insurance, disability, and other protections must be purchased separately
                </div>
              </li>
            </ul>
            <p className="text-blue-900 font-semibold">
              These calculators help you navigate these challenges with data-driven planning rather than guesswork.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-green-100 shadow-md">
            <h3 className="text-2xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <svg className="w-7 h-7 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
              </svg>
              How to Use These Tools Together
            </h3>
            <p className="mb-6 leading-relaxed">
              While each calculator serves a specific purpose, they work best as part of an integrated financial plan:
            </p>
            
            <div className="space-y-5">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-r-2xl shadow-md">
                <h4 className="font-bold text-lg mb-3 text-gray-900 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">1</span>
                  Understand Your Tax Burden (Tax Calculator)
                </h4>
                <p className="text-gray-700">
                  Start with the <Link to="/" className="text-blue-600 underline font-semibold hover:text-blue-800">Tax Calculator</Link> to estimate 
                  your total tax liability including federal income tax, self-employment tax, and state taxes. This shows 
                  you what percentage of income you need to set aside (typically 25-35% for most freelancers).
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-r-2xl shadow-md">
                <h4 className="font-bold text-lg mb-3 text-gray-900 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">2</span>
                  Plan Long-Term Savings (Retirement Calculator)
                </h4>
                <p className="text-gray-700">
                  Use the <Link to="/retirement" className="text-green-600 underline font-semibold hover:text-green-800">Retirement Calculator</Link> to 
                  determine how much you need to save monthly for retirement. Factor in your current age, retirement goals, 
                  and expected returns. Remember: these calculations use real (inflation-adjusted) returns for accuracy.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-6 rounded-r-2xl shadow-md">
                <h4 className="font-bold text-lg mb-3 text-gray-900 flex items-center gap-2">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">3</span>
                  Adjust for Rising Costs (Inflation Calculator)
                </h4>
                <p className="text-gray-700">
                  Check the <Link to="/calculators/inflation" className="text-purple-600 underline font-semibold hover:text-purple-800">Inflation Calculator</Link> to 
                  understand how inflation affects your purchasing power. If inflation averages 3% annually and you don't 
                  raise your rates, you're effectively taking a 3% pay cut each year.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-purple-100 shadow-md">
            <h3 className="text-2xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <svg className="w-7 h-7 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
              Setting Sustainable Freelance Rates
            </h3>
            <p className="mb-6 leading-relaxed">
              Many freelancers underprice their services because they don't account for all costs. Here's a framework 
              for setting profitable rates:
            </p>
            
            <div className="bg-gradient-to-br from-purple-100 via-pink-50 to-purple-50 border-2 border-purple-300 rounded-2xl p-6 md:p-8 mb-6 shadow-lg">
              <h4 className="font-bold text-xl mb-5 text-purple-900 flex items-center gap-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" />
                </svg>
                Rate Calculation Formula:
              </h4>
              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                  <div>
                    <strong className="text-purple-900">Desired annual income:</strong>
                    <span className="text-gray-700"> What you want to take home after all expenses (e.g., $80,000)</span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                  <div>
                    <strong className="text-purple-900">Add business expenses:</strong>
                    <span className="text-gray-700"> Health insurance, software, equipment, office, marketing (~$15,000)</span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                  <div>
                    <strong className="text-purple-900">Add tax burden:</strong>
                    <span className="text-gray-700"> Use Tax Calculator—typically 25-35% of gross income (~$27,000-$38,000)</span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                  <div>
                    <strong className="text-purple-900">Total revenue needed:</strong>
                    <span className="text-gray-700"> $80,000 + $15,000 + $32,000 = </span>
                    <strong className="text-purple-700 text-lg">$127,000</strong>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">5</span>
                  <div>
                    <strong className="text-purple-900">Billable hours:</strong>
                    <span className="text-gray-700"> Assuming 50% utilization (1,000 billable hours/year from 2,000 work hours)</span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">6</span>
                  <div>
                    <strong className="text-purple-900">Minimum hourly rate:</strong>
                    <span className="text-gray-700"> $127,000 ÷ 1,000 = </span>
                    <strong className="text-purple-700 text-2xl">$127/hour</strong>
                  </div>
                </li>
              </ol>
              <p className="mt-5 text-gray-700 italic bg-white/60 p-4 rounded-xl">
                💡 This ensures you cover taxes, expenses, retirement savings, and non-billable time (marketing, admin, professional development).
              </p>
            </div>
            
            <p className="mb-4 leading-relaxed bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-xl">
              <strong className="text-yellow-900">⚠️ Reality Check:</strong> This calculation reveals why many freelancers who charge $50-60/hour struggle financially. After taxes, 
              expenses, and unbillable hours, their effective take-home pay may be less than $30/hour—below minimum 
              wage in some states.
            </p>
            
            <p className="leading-relaxed">
              Use the <Link to="/" className="text-purple-600 underline font-semibold hover:text-purple-800">Tax Calculator</Link> to understand your 
              specific tax burden, then factor in your actual business expenses and billable hours percentage to 
              determine a rate that supports your financial goals.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-red-100 shadow-md">
            <h3 className="text-2xl font-bold mb-5 text-gray-900 flex items-center gap-2">
              <svg className="w-7 h-7 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
              </svg>
              Common Mistakes Freelancers Make
            </h3>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">Not saving for quarterly taxes:</strong>
                  <span className="text-gray-700"> April surprise tax bills can devastate cash flow. Set aside 25-35% of every payment.</span>
                </div>
              </li>
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">Ignoring retirement:</strong>
                  <span className="text-gray-700"> Without an employer match, you need to save aggressively—15-20% of income minimum.</span>
                </div>
              </li>
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">Static pricing:</strong>
                  <span className="text-gray-700"> Not raising rates annually for inflation means losing 2-3% purchasing power each year.</span>
                </div>
              </li>
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">Confusing revenue with profit:</strong>
                  <span className="text-gray-700"> Gross income isn't take-home pay. Account for taxes, expenses, and savings first.</span>
                </div>
              </li>
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">No emergency fund:</strong>
                  <span className="text-gray-700"> Irregular income requires 6-12 months of expenses saved, not just 3-6 months.</span>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-2">
              <svg className="w-7 h-7 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Additional Resources
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/about" className="group block bg-white border-2 border-blue-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">Understanding 1099 Taxes</h4>
                </div>
                <p className="text-gray-600">Complete guide to self-employment tax, deductions, and quarterly payments</p>
              </Link>
              <Link to="/faq" className="group block bg-white border-2 border-green-200 rounded-2xl p-6 hover:border-green-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-green-600 transition-colors">Frequently Asked Questions</h4>
                </div>
                <p className="text-gray-600">Quick answers about quarterly taxes, deductions, and tax planning</p>
              </Link>
              <Link to="/trends" className="group block bg-white border-2 border-purple-200 rounded-2xl p-6 hover:border-purple-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-purple-600 transition-colors">Job Market Trends</h4>
                </div>
                <p className="text-gray-600">Industry growth data and salary benchmarks for freelancers</p>
              </Link>
              <Link to="/blog/external" className="group block bg-white border-2 border-orange-200 rounded-2xl p-6 hover:border-orange-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-orange-600 transition-colors">Official Tax Resources</h4>
                </div>
                <p className="text-gray-600">Links to IRS, SSA, BLS, and FINRA for authoritative guidance</p>
              </Link>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-500 p-6 rounded-r-2xl shadow-md">
            <div className="flex gap-3">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-gray-800 leading-relaxed">
                <strong className="text-yellow-900">Disclaimer:</strong> These calculators provide educational estimates only. Some content was generated 
                with AI assistance and reviewed for accuracy. Results do not constitute tax, legal, or financial advice. 
                Always consult a qualified CPA, Enrolled Agent, or financial advisor for personalized guidance on taxes, 
                retirement planning, and financial decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <RecentPosts />
    </main>
  );
}
